import type { Auth, User } from "firebase/auth";

import { auth } from "../firebase";

// CRITICAL: This file is part of a delicate dependency chain.
// It imports 'auth' from firebase.ts.
// Therefore, firebase.ts MUST NOT import anything that depends on this file (like the store or baseApi).
// See authStoreSync.ts for how we decoupled the store from firebase.ts.

/**
 * Small wrapper around Firebase `Auth` that centralizes common auth operations.
 *
 * @remarks
 * This wrapper exists primarily to:
 * - keep a single import path for auth/token access (`getIdToken()`),
 * - prevent accidental direct usage of `getAuth()` in most modules (see ESLint rule), and
 * - make the firebase/bootstrap dependency chain explicit and stable.
 *
 * @see src/lib/firebase.ts
 * @see src/lib/firebase/authStoreSync.ts
 */
class MemoizedAuth {
  private readonly authInstance: Auth;

  constructor(authInstance: Auth) {
    this.authInstance = authInstance;
  }

  getAuth(): Auth {
    return this.authInstance;
  }

  getCurrentUser(): User | null {
    return this.authInstance.currentUser;
  }

  /**
   * Returns the current user's ID token if authenticated.
   *
   * @remarks
   * Prefer this over calling `getAuth().currentUser?.getIdToken()` to keep call sites consistent.
   */
  async getIdToken(forceRefresh = false): Promise<string | null> {
    const user = this.getCurrentUser();

    if (!user) {
      return null;
    }

    return user.getIdToken(forceRefresh);
  }

  async signOut(): Promise<void> {
    await this.authInstance.signOut();
  }

  isAuthenticated(): boolean {
    return !!this.authInstance.currentUser;
  }
}

const memoizedAuth = new MemoizedAuth(auth);

export default memoizedAuth;
